import { useState } from 'react';
import {
  CapaErrorCode,
  CapaServiceType,
  CapaWithdrawClientReasonType,
  CapaWithdrawOutput,
  FetchWithdrawnChatRoomsInput,
  FetchWithdrawnChatRoomsQuery,
  useWithdrawQuotationUserMutation
} from '@generated/graphql';
import { WithdrwalStage } from '../useWithdrawalStage';
import ConfirmChatPresenter from './ConfirmChatPresenter';
import useServiceTab from './useServiceTab';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from '@components/Provider';
import WithdrawalConfirmDialog from '../WithdrawalConfirmDialog';
import getScopes from '@utils/getScopes';
import useClient from '@hooks/useClient';
import { apolloClient } from '@api/apollo';
import { PreventEditDialog } from '../../Dialog/RequestActionDialog';

interface Props {
  matches: boolean;
  data: FetchWithdrawnChatRoomsQuery | undefined;
  setStage: (stage: WithdrwalStage) => void;
  getChatRoom: (variables: FetchWithdrawnChatRoomsInput) => void;
  onChangeRoute: (path: string) => void;
}

export interface FormValues {
  consulting: boolean;
  group: boolean;
}

const ConfirmChatContainer = ({
  matches,
  data,
  getChatRoom,
  onChangeRoute
}: Props) => {
  const { onInitClient } = useClient();
  const { email, scopes } = getScopes();

  const [pagination, setPagination] = useState({ offset: 0, limit: 5 });
  const [serviceTab, setServiceTab] = useServiceTab(() =>
    setPagination({ offset: 0, limit: 5 })
  );

  getChatRoom({
    limit: pagination.limit,
    offset: pagination.offset,
    serviceType: serviceTab
  });

  const [open, setOpen] = useState(false);
  const [preventModal, setPreventModal] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<FormValues>({
    defaultValues: {
      consulting: false,
      group: false
    }
  });

  const filteredScopes = scopes.filter(
    (scope) => scope !== CapaServiceType.Cloud
  );

  const isConnect = filteredScopes.includes(CapaServiceType.Connect);

  const [withdrawQuotationUser] = useWithdrawQuotationUserMutation();

  const onSubmit = methods.handleSubmit(
    (data) => {
      setOpen(true);
    },
    (error) => {
      const isError = Object.keys(error).length > 0;

      if (isError) {
        enqueueSnackbar('체크를 완료해야 탈퇴를 진행할 수 있습니다.', {
          variant: 'error'
        });
      }
    }
  );

  const onClickPage = (value: number) => {
    return setPagination({ limit: 5, offset: (value - 1) * 5 });
  };

  const onWithdraw = async () => {
    const getItem = sessionStorage.getItem('withdraw');

    if (getItem) {
      const parsed: {
        reason: CapaWithdrawClientReasonType;
        etcReason: string;
      } = JSON.parse(getItem);

      if (parsed?.reason) {
        await withdrawQuotationUser({
          variables: {
            input: {
              email: email,
              withdrawReason: {
                serviceType: CapaServiceType.Quotation,
                reason: parsed.reason,
                etcReason: parsed.etcReason
              }
            }
          }
        }).then(({ data }) => {
          const result = data?.withdrawQuotationUser.result;

          if (result === CapaErrorCode.Success) {
            sessionStorage.clear();
            localStorage.clear();
            apolloClient.clearStore();
            onInitClient();

            return onChangeRoute('/mypage/withdrawal-complete');
          }
          if (result === CapaErrorCode.RfqPaymentInProgress) {
            setOpen(false);
            setPreventModal(true);
          }
        });
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <WithdrawalConfirmDialog
        matches={matches}
        open={open}
        onClose={() => setOpen(false)}
        onWithdraw={onWithdraw}
      />
      <PreventEditDialog
        open={preventModal}
        onClose={() => setPreventModal(false)}
        description={`캐파 회원 탈퇴신청이 불가능 합니다.\n파트너와 거래를 완료하거나 거래취소를 진행해주세요.`}
      />
      <ConfirmChatPresenter
        matches={matches}
        data={
          (data?.fetchWithdrawnChatRooms.data as CapaWithdrawOutput) ??
          undefined
        }
        scopes={scopes}
        serviceTab={serviceTab}
        isConnect={isConnect}
        pagination={pagination}
        setServiceTab={setServiceTab}
        onChangeRoute={onChangeRoute}
        onSubmit={onSubmit}
        onClickPage={onClickPage}
      />
    </FormProvider>
  );
};

export default ConfirmChatContainer;
